import React, { useEffect, useRef, useState } from 'react';

export interface MenuOption {
    value: string;
    label: string;
    /** Colore del pallino accanto all'etichetta; assente = nessun pallino. */
    dot?: string;
}

interface Props {
    options: MenuOption[];
    /** Valore attuale: segnato con ✓. Assente nei menu di gruppo. */
    value?: string;
    onPick: (value: string) => void;
    /** Contenuto del pulsante che apre il menu. */
    trigger: React.ReactNode;
    triggerClassName: string;
    ariaLabel: string;
    disabled?: boolean;
}

/**
 * Pulsante con menu a scelta singola, per le celle della griglia e la barra di gruppo.
 * Tastiera: Invio/Spazio o freccia giù aprono, frecce scorrono, Esc chiude e torna al pulsante.
 */
const OptionMenu: React.FC<Props> = ({ options, value, onPick, trigger, triggerClassName, ariaLabel, disabled }) => {
    const [open, setOpen] = useState(false);
    const root = useRef<HTMLDivElement>(null);
    const button = useRef<HTMLButtonElement>(null);
    const items = useRef<(HTMLButtonElement | null)[]>([]);

    useEffect(() => {
        if (!open) return;
        const current = Math.max(0, options.findIndex(o => o.value === value));
        items.current[current]?.focus();
        const onDown = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open, options, value]);

    const close = (refocus: boolean) => {
        setOpen(false);
        if (refocus) button.current?.focus();
    };

    const onMenuKey = (e: React.KeyboardEvent) => {
        const index = items.current.findIndex(el => el === document.activeElement);
        if (e.key === 'Escape') { e.preventDefault(); close(true); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); items.current[(index + 1) % options.length]?.focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); items.current[(index - 1 + options.length) % options.length]?.focus(); }
        else if (e.key === 'Home') { e.preventDefault(); items.current[0]?.focus(); }
        else if (e.key === 'End') { e.preventDefault(); items.current[options.length - 1]?.focus(); }
        else if (e.key === 'Tab') close(false);
    };

    return (
        <div className="cat-menu" ref={root}>
            <button
                ref={button}
                type="button"
                className={triggerClassName}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={ariaLabel}
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                onKeyDown={e => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
            >
                {trigger}
            </button>
            {open && (
                <div className="cat-menu__list" role="menu" aria-label={ariaLabel} onKeyDown={onMenuKey}>
                    {options.map((option, i) => {
                        const current = option.value === value;
                        return (
                            <button
                                key={option.value}
                                ref={el => { items.current[i] = el; }}
                                type="button"
                                role={value === undefined ? 'menuitem' : 'menuitemradio'}
                                aria-checked={value === undefined ? undefined : current}
                                className="cat-menu__item"
                                onClick={() => { close(true); if (!current) onPick(option.value); }}
                            >
                                {option.dot && <span className="cat-dot" style={{ background: option.dot }} aria-hidden="true" />}
                                <span className="cat-menu__label">{option.label}</span>
                                {current && <i className="bi bi-check2" aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default OptionMenu;
